import { abyssColors } from '@/theme/colors';
import { theme } from '@/theme/theme';
import {
  Box,
  Button,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Typography,
  styled as muiStyled,
} from '@mui/material';

type SliderProps = {
  open: boolean;
};

export const OverrideSection = muiStyled(Stack)(() => ({
  padding: '3.2rem 2rem 2.4rem 2.4rem',
  gap: '1.6rem',
  position: 'relative',
}));

export const LineBreak = muiStyled(Box)(() => ({
  height: '0.1rem',
  width: 'calc(100% - 1.6rem)',
  background: abyssColors.primary[300],
  opacity: 0.2,
  margin: '0 0.8rem',
}));

export const Title = muiStyled(Typography)(() => ({
  fontSize: '1.6rem',
  fontWeight: 500,
  color: abyssColors.primary[500],
  lineHeight: 'normal',
  whiteSpace: 'nowrap',
}));

export const CustomSwitch = muiStyled(Switch)(() => ({
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: '#2690ca',
    '&:hover': {
      backgroundColor: 'rgba(38, 144, 202, 0.08)',
    },
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: '#82badb',
  },
  '& .MuiSwitch-track': {
    backgroundColor: '#999a9c',
  },
  '& .MuiSwitch-thumb': {
    boxShadow:
      '0px 1px 3px rgba(0, 0, 0, 0.12), 0px 1px 1px rgba(0, 0, 0, 0.14), 0px 2px 1px rgba(0, 0, 0, 0.20)',
  },
}));

export const RadioLabel = muiStyled(Typography)(() => ({
  fontSize: '1.4rem',
  fontWeight: 400,
  color: abyssColors.primary[500],
  lineHeight: 'normal',
}));

export const CustomRadioGroup = muiStyled(RadioGroup)(() => ({
  display: 'flex',
  gap: '0.5rem',
}));

export const CustomRadio = muiStyled(Radio)(() => ({
  color: '#7e9bbb',
  '&.Mui-checked': {
    color: '#0c7cbb',
  },
}));

export const CustomSlider = muiStyled(Box)<SliderProps>(({ open }) => ({
  overflow: 'hidden',
  maxHeight: open ? '100vh' : '0px',
  opacity: open ? 1 : 0,
  transition: 'all 500ms ease',
}));

// PostInspectionMedia styles

export const MediaWrapperContainer = muiStyled(Stack)(() => ({
  width: '100%',
  paddingTop: '1.6rem',
  paddingBottom: '1rem',
  gap: '0.75rem',
}));

export const MediaSectionTitle = muiStyled(Typography)(() => ({
  fontSize: '1.4rem',
  fontWeight: 500,
  color: abyssColors.primary[500],
}));

export const UploadButton = muiStyled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '30.2rem',
  height: '3.2rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.625rem',
  padding: '0 1rem',
  marginTop: '1rem',
  marginBottom: '1rem',
  cursor: 'pointer',
  boxSizing: 'border-box',
  borderRadius: '0.4rem',
  border: `1px solid ${abyssColors.primary[500]}`,
  backgroundColor: theme.palette.background.paper,
  transition: 'background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease',
  '&:hover': {
    backgroundColor: abyssColors.primary[50],
    border: `1px solid ${abyssColors.primary[500]}`,
    boxShadow: '0 1px 6px rgba(34, 64, 99, 0.06)',
  },
  '&:focus-visible': {
    outline: `2px solid ${abyssColors.primary[100]}`,
    outlineOffset: '2px',
  },
}));

export const UploadButtonText = muiStyled(Typography)(() => ({
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '1.3rem',
  lineHeight: '1',
  color: abyssColors.primary[600],
}));

export const NotesLabel = muiStyled(Typography)(() => ({
  fontWeight: 500,
  fontSize: '1.2rem',
  color: abyssColors.primary[500],
}));

export const NotesContainer = muiStyled(Box)(() => ({
  border: `1px solid ${abyssColors.primary[300]}`,
  width: '100%',
  borderRadius: '0.25rem',
  minHeight: '2.8rem',
  padding: '0.8rem',
  backgroundColor: theme.palette.background.paper,
  overflow: 'visible',
}));

export const notesTextareaStyles: React.CSSProperties = {
  width: '100%',
  border: 'none',
  outline: 'none',
  resize: 'none',
  fontFamily: 'inherit',
  fontSize: '1.2rem',
  color: abyssColors.primary[500],
  backgroundColor: 'transparent',
  lineHeight: '1.2',
  overflow: 'hidden',
  padding: 0,
  margin: 0,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

export const NotesDisplayText = muiStyled(Typography)(() => ({
  color: abyssColors.primary[500],
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  fontSize: '1.2rem',
}));

export const ActionTextButton = muiStyled(Button)(() => ({
  minWidth: '7.5rem',
  height: '3.4rem',
  border: `1px solid ${abyssColors.primary[500]}`,
  padding: '0 1rem',
  borderRadius: '0.5rem',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '1.2rem',
  color: abyssColors.primary[500],
  backgroundColor: 'transparent',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: abyssColors.primary[50],
  },
  '&:focus-visible': {
    boxShadow: `0 0 0 4px ${abyssColors.primary[50]}`,
  },
  '&.Mui-disabled': {
    color: abyssColors.neutral[300],
    backgroundColor: 'transparent',
  },
}));

export const ActionPrimaryButton = muiStyled(Button)(() => ({
  minWidth: '7.5rem',
  height: '3.4rem',
  padding: '0 1rem',
  borderRadius: '0.5rem',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '1.2  rem',
  color: abyssColors.neutral[50],
  backgroundColor: abyssColors.primary[600],
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: abyssColors.primary[700],
    boxShadow: '0 2px 8px rgba(34, 64, 99, 0.12)',
  },
  '&:focus-visible': {
    boxShadow: `0 0 0 4px ${abyssColors.primary[50]}`,
  },
  '&.Mui-disabled': {
    backgroundColor: abyssColors.primary[100],
    color: abyssColors.neutral[300],
  },
}));
